import {
  AdgroupFormData,
  CampaignFormData,
  CampaignQuotaType,
  CreateMode,
  Ios14Quota,
  IOS14QuotaType,
  IOS14Type,
} from '@tt4b/ttam-enums';
import {
  NetStateEnum,
  useAdgroupIos14,
  useAdgroupMode,
  useAllowlist,
  useIOS14QuotaApi,
  useRequest,
  useStdObjectiveType,
  useStore,
  useSplitTestEnabled,
} from '@tt4b/ttam-hooks';
import { Ref, ref, computed, onMounted, watch } from '@vue/composition-api';
import { i18n } from './../../i18n';

const { getStore } = useStore();

export function usePropsIos14Disabled(options: {
  campaignData: Ref<CampaignFormData>;
  adgroupData: Ref<AdgroupFormData>;
}) {
  const { campaignData, adgroupData } = options;
  const { isIOS14Disabled } = useAdgroupIos14({
    campaignFormData: campaignData,
    adgroupFormData: adgroupData,
  });
  return {
    isIOS14Disabled,
  };
}

export function usePropsIos14DisabledHint(options: { campaignData: Ref<CampaignFormData> }) {
  const { campaignData } = options;
  const { isConversion } = useStdObjectiveType({
    campaignFormData: campaignData,
  });
  const disabledIos14Hint = computed(() => {
    if (isConversion.value) {
      return i18n.t('module_adgroup_ios14_switch_conversion_disable_hint');
    }

    return i18n.t('module_adgroup_ios14_switch_disable_hint');
  });

  return {
    disabledIos14Hint,
  };
}

export function usePropsProfilePageDisabled(options: {
  createMode: Ref<CreateMode>;
  campaignData: Ref<CampaignFormData>;
}) {
  const { createMode, campaignData } = options;

  const { isEdit } = useAdgroupMode({
    createMode,
  });

  const disabledProfilePage = computed(
    () => isEdit.value || campaignData.value.campaignAppProfilePageType !== undefined,
  );
  return {
    disabledProfilePage,
  };
}

export function usePropsEncourageInfoVisible(options: { adgroupData: Ref<AdgroupFormData> }) {
  const { adgroupData } = options;

  const encourageInfoVisible = computed(() => adgroupData.value.ios14QuotaType === IOS14QuotaType.UNOCCUPIED);

  return {
    encourageInfoVisible,
  };
}

export function usePropsAppProfilePageVisible(options: { adgroupData: Ref<AdgroupFormData> }) {
  const { adgroupData } = options;

  const appProfilePageVisible = computed(() => adgroupData.value.ios14QuotaType === IOS14QuotaType.OCCUPIED);

  return {
    appProfilePageVisible,
  };
}

const getQuotaConfig = (ios14Quota: Ref<Ios14Quota>) => {
  const quotaNum = computed(() => ({
    usedNum: ios14Quota.value?.campaignQuota?.usedQuota ?? 0,
    leftNum: ios14Quota.value?.campaignQuota?.availableQuota ?? 0,
    releaseNum: ios14Quota.value?.campaignQuota?.releasingQuota ?? 0,
  }));
  const quotaConfig = computed(() => ({
    [CampaignQuotaType.EMPTY]: {
      type: 'error',
      title: i18n.t('module_ios14_campaign_quota_empty'),
      description: i18n.t('module_ios14_campaign_quota_empty_info', quotaNum.value),
    },
    [CampaignQuotaType.INSUFFICIENT]: {
      type: 'warning',
      title: i18n.t('module_ios14_campaign_quota_insufficient', quotaNum.value),
      description: i18n.t('module_ios14_campaign_quota_insufficient_info', quotaNum.value),
    },
    [CampaignQuotaType.SUFFICIENT]: {
      type: 'info',
      title: i18n.t('module_ios14_campaign_quota_sufficient'),
      description: i18n.t('module_ios14_campaign_quota_sufficient_info', quotaNum.value),
    },
  }));
  const isSufficientQuota =
    quotaNum.value.leftNum > CampaignQuotaType.INSUFFICIENT
      ? quotaConfig.value[CampaignQuotaType.SUFFICIENT]
      : quotaConfig.value[CampaignQuotaType.INSUFFICIENT];

  const result = computed(() =>
    quotaNum.value.leftNum <= CampaignQuotaType.EMPTY ? quotaConfig.value[CampaignQuotaType.EMPTY] : isSufficientQuota,
  );

  return result;
};

export function usePropsQuotaTitle(ios14Quota: Ref<Ios14Quota>) {
  const quotaTitle = computed(() => getQuotaConfig(ios14Quota).value.title);
  return {
    quotaTitle,
  };
}

export function usePropsQuotaDesc(ios14Quota: Ref<Ios14Quota>) {
  const quotaDescription = computed(() => getQuotaConfig(ios14Quota).value.description);
  return {
    quotaDescription,
  };
}

export function usePropsQuotaType(ios14Quota: Ref<Ios14Quota>) {
  const quotaType = computed(() => getQuotaConfig(ios14Quota).value.type);
  return {
    quotaType,
  };
}

export function usePropsQuotaAlertVisible(options: { adgroupData: Ref<AdgroupFormData>; isFrozenApp: Ref<boolean> }) {
  const quotaAlertVisible = computed(() => false);

  return {
    quotaAlertVisible,
  };
}

export function usePropsIos14Quota(options: {
  campaignData: Ref<CampaignFormData>;
  adgroupData: Ref<AdgroupFormData>;
}) {
  const { campaignData, adgroupData } = options;

  const { isSplitTestEnabled } = useSplitTestEnabled(options);
  const appId = computed(() => adgroupData.value.adRefAppId);
  const isSplitTest = computed(() => isSplitTestEnabled.value);
  const campaignId = computed(() => campaignData.value.campaignId);

  const { data, run } = getStore(
    NetStateEnum.ios14Quota,
    {
      appId: appId.value,
      campaignId: campaignId.value,
      isSplitTest: isSplitTest.value,
    },
    false,
    false,
  );

  const retry = () => {
    run({
      appId: appId.value,
      isSplitTest: isSplitTest.value,
    });
  };

  watch(appId, newVal => {
    if (newVal) {
      retry();
    }
  });

  return {
    ios14Quota: data,
  };
}
